import { useState } from "react";
import Reveal from "@/components/landing/Reveal";
import { LEAD_MAGNET_IMAGE, WEB3FORMS_KEY } from "@/lib/content";

export default function LeadMagnet() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [errorMsg, setErrorMsg] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");
        try {
            // Use URLSearchParams to send as application/x-www-form-urlencoded
            // This avoids CORS preflight (simple request)
            const params = new URLSearchParams();
            params.append("access_key", WEB3FORMS_KEY);
            params.append("subject", `New guide request from ${name || "a website visitor"}`);
            params.append("from_name", "Arc & Light — Sacred Home Guide");
            params.append("replyto", email);
            params.append("botcheck", "");
            params.append("Source", "The Sacred Home — guide request");
            params.append("Name", name);
            params.append("Email", email);

            const res = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: params.toString(),
            });
            const json = await res.json();
            if (json.success) {
                setStatus("sent");
                setName("");
                setEmail("");
            } else {
                setStatus("error");
                setErrorMsg(json.message || "Something went wrong. Please try again.");
            }
        } catch (err) {
            setStatus("error");
            setErrorMsg("Network error. Please try again, or email merilyn@merilyn.com.au directly.");
        }
    };

    return (
        <section
            data-testid="lead-magnet-section"
            className="bg-pearl py-32 md:py-44"
        >
            <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                <Reveal className="lg:col-span-6">
                    <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0 bg-pearl-soft overflow-hidden">
                        <img
                            src={LEAD_MAGNET_IMAGE}
                            alt="The Sacred Home — a quiet study of materiality"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-10 bg-gradient-to-b from-pearl/0 via-pearl/0 to-pearl/55">
                            <span className="font-serif text-clay-deep text-[0.7rem] tracking-[0.3em] uppercase">
                                A guide
                            </span>
                            <div>
                                <p className="font-serif text-pearl text-[1.8rem] md:text-[2.2rem] leading-[1.05] drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
                                    The Sacred
                                    <span className="block italic text-clay-deep">Home.</span>
                                </p>
                                <p className="mt-3 text-cacao/70 text-[0.78rem] tracking-[0.18em] uppercase">
                                    PDF · 12 pages
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <div className="lg:col-span-6">
                    <Reveal>
                        <p className="eyebrow text-cacao/60 mb-8">
                            <span className="divider-line text-cacao/40 mr-4 align-middle" />
                            Not ready to begin?
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <h2
                            data-testid="lead-magnet-headline"
                            className="font-serif text-cacao text-[2.2rem] sm:text-[2.8rem] md:text-[3.4rem] leading-[1.05] max-w-[18ch]"
                        >
                            Start with{" "}
                            <em className="italic text-clay-deep">five quiet shifts.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={220}>
                        <p className="mt-8 text-cacao/75 text-[1rem] leading-[1.85] font-light max-w-[32rem]">
                            Download <em className="italic">The Sacred Home</em> — five quiet shifts that make any space feel finished. A small taste of how Arc &amp; Light sees.
                        </p>
                    </Reveal>

                    <Reveal delay={320}>
                        <form
                            onSubmit={onSubmit}
                            data-testid="lead-magnet-form"
                            className="mt-12 max-w-[32rem]"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                                <input
                                    type="text"
                                    placeholder="First name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="field-line"
                                    data-testid="lead-magnet-name"
                                    required
                                />
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="field-line"
                                    data-testid="lead-magnet-email"
                                    required
                                />
                            </div>
                            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
                                <button
                                    type="submit"
                                    disabled={status === "sending" || status === "sent"}
                                    className="btn-clay disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="lead-magnet-submit"
                                >
                                    {status === "sending" ? "Sending…" : status === "sent" ? "Sent" : "Send me the guide"}
                                </button>
                                <p className="text-cacao/55 text-[0.78rem] italic font-serif">
                                    No spam. Just considered ideas, occasionally.
                                </p>
                            </div>
                            {status === "sent" && (
                                <p
                                    data-testid="lead-magnet-confirmation"
                                    className="mt-8 text-clay-deep italic font-serif text-[1.05rem]"
                                >
                                    Thank you — your guide will be on its way soon.
                                </p>
                            )}
                            {status === "error" && (
                                <p
                                    data-testid="lead-magnet-error"
                                    className="mt-8 text-clay-deep italic font-serif text-[1rem]"
                                >
                                    {errorMsg}
                                </p>
                            )}
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
